import memoize from 'memoize-one';

import { fg } from '@confluence/feature-gating';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';

import type {
	ExperienceAttributes,
	ExperienceEvent,
	StartEvent,
	StopEvent,
} from './ExperienceEvent.ts';
import { ExperienceTimeoutError } from './ExperienceTimeoutError.ts';
import { getTabInactivityTracker } from './TabInactivityTracker.ts';
import { getFreezeTracker } from './FreezeTracker.ts';
import type { ExperienceStopStates } from './ExperienceStopStates.ts';
import {
	CREATE_PAGE_EXPERIENCE,
	VIEW_CONTENT_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
	SPACE_OVERVIEW_EXPERIENCE,
	VIEW_PAGE_SESSION_EXPERIENCE,
	WHITEBOARD_EXPERIENCE,
	WHITEBOARD_CONTENT_EXPERIENCE,
	SLIDE_EXPERIENCE,
	SLIDE_CONTENT_EXPERIENCE,
	SELECT_INLINE_COMMENT_EXPERIENCE,
	SHARE_AND_RESTRICT_DIALOG_LOAD_EXPERIENCE,
	CREATE_PAGE_FROM_LINK_CREATION_EXPERIENCE,
	FOLDER_EXPERIENCE,
} from './ExperienceName.ts';
import { getResponderForErrorAttribution } from './getResponderForErrorAttribution.ts';

type ConstructorProps = {
	name: string;
	id: string;
	timeout?: number;
	startTime?: number;
	attributes?: ExperienceAttributes;
	onStart?: (event: StartEvent) => (event: StopEvent) => void;
	onSuccess?: () => void;
	onFailure?: () => void;
	onAbort?: () => void;
};

type ExperienceStopStateKeys = keyof typeof ExperienceStopStates;
type ExperienceStopState = (typeof ExperienceStopStates)[ExperienceStopStateKeys];

export type ExperienceState = {
	taskId: string;
	name: string;
	timeout?: number;
	hasStopped: boolean;
	stopState: ExperienceStopState | null;
	startTime: number;
	attributes?: ExperienceAttributes;
};

export type DebugPoint = {
	timestamp: string;
	now: number;
	message: string;
	attributes: ExperienceAttributes;
};

// Whenever we want to target view-page experience, we should ensure we're targeting all correlated view-page experiences.
// This is more complete and helps avoid bugs related to view-content experience forwarding to view-page.
const VIEW_PAGE_EXPERIENCES = [
	VIEW_PAGE_EXPERIENCE,
	VIEW_CONTENT_EXPERIENCE,
	VIEW_PAGE_SESSION_EXPERIENCE,
];
// Experience timeouts by default are considered taskFails. Certain experiences should instead treat timeouts as aborts, to reduce unnecessary noise and improve SLI accuracy.
const EXPERIENCE_TIMEOUTS_TO_ABORT = [
	CREATE_PAGE_EXPERIENCE,
	CREATE_PAGE_FROM_LINK_CREATION_EXPERIENCE,
	...VIEW_PAGE_EXPERIENCES,
	WHITEBOARD_EXPERIENCE,
	WHITEBOARD_CONTENT_EXPERIENCE,
	SLIDE_EXPERIENCE,
	SLIDE_CONTENT_EXPERIENCE,
	SPACE_OVERVIEW_EXPERIENCE,
	SHARE_AND_RESTRICT_DIALOG_LOAD_EXPERIENCE,
	FOLDER_EXPERIENCE,
];

// Errors that are thrown by client side and can be ignored, this is to avoid treating these as fails that can impact SLOs.
// ideally we can show the user a flag that shows something is wrong, please avoid adding to this list unless absolutely necessary.
const IGNORABLE_CLIENT_SIDE_ERRORS = ["can't access dead object"];

// HOT-127428: a third-party browser extension / DLP agent patches `appendChild` to `JSON.stringify`
// inserted DOM nodes, which throws on React's circular `__reactFiber$` refs during commit. This is a
// client-environment issue (not our code; seen across multiple tenants/products) that we cannot fix,
// so we abort rather than fail — keeping it out of SLOs. Matched narrowly (message AND `__reactFiber$`)
// behind a gate so it can't mask a genuine in-house circular-JSON regression.
function isIgnorableClientSideError(message: string | undefined): boolean {
	if (!message) {
		return false;
	}
	if (IGNORABLE_CLIENT_SIDE_ERRORS.includes(message)) {
		return true;
	}
	return (
		message.includes('Converting circular structure to JSON') &&
		message.includes('__reactFiber$') &&
		fg('confluence_ignore_third_party_dom_patch_errors')
	);
}

/**
 * A list of experiences that should have debugMessages attached to abort events.
 *
 * This is not a safe default, as at time of addition ~20% of experiences
 * with some events having ~90% abort rates with high volume (>10k per 15m)
 *
 * If adding -- ensure you first review the event and abort volume.
 */
const EXPERIENCES_TO_DEBUG_ON_ABORT = [SELECT_INLINE_COMMENT_EXPERIENCE];

export class Experience {
	name: string;
	id: string;
	timeout?: number;

	private freezeTime = 0;
	private stopState: ExperienceStopState | null = null;
	private startTime: number;
	private attributes?: ExperienceAttributes;
	private onStop?: (event: StopEvent) => void;
	private onSuccess?: () => void;
	private onFailure?: () => void;
	private onAbort?: () => void;
	private debugPoints: DebugPoint[] = [];

	constructor({
		name,
		id,
		timeout,
		startTime = typeof window !== 'undefined' ? window.performance.now() : 0,
		attributes,
		onStart,
		onSuccess,
		onFailure,
		onAbort,
	}: ConstructorProps) {
		this.name = name;
		this.id = id;
		this.timeout = timeout;
		this.startTime = startTime;
		this.attributes = attributes;
		this.onSuccess = onSuccess;
		this.onFailure = onFailure;
		this.onAbort = onAbort;

		const startEvent: StartEvent = {
			action: 'taskStart',
			name,
			id,
			startTime,
			timeout,
			attributes: {
				...getUniquePageLoadId(),
				...attributes,
				...this.getFeatureGateAttributes(),
			},
		};
		if (onStart) {
			this.onStop = onStart(startEvent);
		}
		getFreezeTracker().subscribe(name, (time) => {
			this.freezeTime += time;
		});
	}

	succeed(attributes?: ExperienceAttributes, publishDebugPoints?: boolean): void {
		if (this.hasStopped) return;

		this.onSuccess && this.onSuccess();

		this.stop({
			action: 'taskSuccess',
			name: this.name,
			id: this.id,
			startTime: this.startTime,
			duration: this.getAbsoluteDuration(),
			activeDuration: this.getDurationAdjustedForActive(),
			adjustedDuration: this.getDurationAdjustedForTabActivity(),
			...(publishDebugPoints && { debugPoints: this.debugPoints }),
			attributes: {
				...getUniquePageLoadId(),
				...this.getFeatureGateAttributes(),
				...this.attributes,
				...attributes,
			},
		});
	}

	fail({ error, attributes }: { error: Error; attributes?: ExperienceAttributes }): void {
		if (this.hasStopped) return;

		if (isIgnorableClientSideError(error?.message)) {
			this.abort({
				reason: 'Aborted because of client side error',
				attributes: {
					...attributes,
					clientSideError: error.message,
				},
			});
			return;
		}

		this.onFailure && this.onFailure();

		const alertResponder = fg('confluence_experience_responder_routing')
			? getResponderForErrorAttribution(
					attributes?.errorAttribution ?? this.attributes?.errorAttribution,
				)
			: undefined;

		this.stop({
			action: 'taskFail',
			name: this.name,
			id: this.id,
			startTime: this.startTime,
			duration: this.getAbsoluteDuration(),
			activeDuration: this.getDurationAdjustedForActive(),
			adjustedDuration: this.getDurationAdjustedForTabActivity(),
			debugPoints: this.debugPoints,
			error,
			attributes: {
				...getUniquePageLoadId(),
				...this.getFeatureGateAttributes(),
				...this.attributes,
				...attributes,
				...(alertResponder ? { alertResponder } : {}),
			},
		});
	}

	abort({
		reason,
		attributes,
		checkForTimeout = true,
		/**
		 * @deprecated
		 * This will not attach debug points in the event that the experience in scenarios where
		 * the abort is not coming from consumer code (e.g. when the experience is stopped due
		 * to a new experience starting).
		 */
		publishDebugPoints = false,
	}: {
		reason: string;
		attributes?: ExperienceAttributes;
		checkForTimeout?: boolean;
		publishDebugPoints?: boolean;
	}): void {
		if (this.hasStopped) return;

		const adjustedDuration = this.getDurationAdjustedForTabActivity();
		const isTimeout = checkForTimeout && this.timeout != null && adjustedDuration >= this.timeout;

		// Check if the experience should have failed due to timeout
		if (isTimeout) {
			// We want to abort timeouts from view-page and create-page to improve SLO reliability accuracy as they're not guaranteed to be correlated to real user impact. For more info see MODES-4806.
			if (!EXPERIENCE_TIMEOUTS_TO_ABORT.includes(this.name)) {
				this.fail({
					attributes: {
						originalAbortReason: reason,
						...attributes,
					},
					error: new ExperienceTimeoutError(`${this.name} failed to complete in ${this.timeout}ms`),
				});
				if (process.env.NODE_ENV === 'development') {
					// eslint-disable-next-line no-console
					console.warn(
						`%cExperience Timeout: ${this.name} failed to complete in ${this.timeout}ms`,
						`color: #FF0000; font-size: 20px; font-weight: bold;`,
					);
					// eslint-disable-next-line no-console
					console.warn(
						`%cWhile timeout can happen due to genuine reasons, often this is an indication that experience is started and NOT stopped. If you see this error, please ensure that mentioned experience is stopped correctly.`,
						`color: #FF0000;`,
					);
				}

				return;
			}
		}

		this.onAbort && this.onAbort();

		this.stop({
			action: 'taskAbort',
			name: this.name,
			id: this.id,
			startTime: this.startTime,
			duration: this.getAbsoluteDuration(),
			activeDuration: this.getDurationAdjustedForActive(),
			adjustedDuration,
			...(EXPERIENCES_TO_DEBUG_ON_ABORT.includes(this.name)
				? {
						debugPoints: this.debugPoints,
					}
				: {}),
			...(publishDebugPoints && { debugPoints: this.debugPoints }),
			reason: isTimeout
				? `ExperienceTimeout: ${this.name} failed to complete in ${this.timeout}ms`
				: reason,
			checkForTimeout,
			attributes: {
				originalAbortReason: isTimeout ? reason : undefined,
				...getUniquePageLoadId(),
				...this.getFeatureGateAttributes(),
				...this.attributes,
				...attributes,
			},
		});
	}

	/**
	 * Called on experience to stop it with the same action (success / failure / abort) and attributes as another event.
	 * For example, compound experience (e.g. edit-page) is failed if sub-experience (e.g. edit-page/publish) fails.
	 */
	stopOn(event?: ExperienceEvent, extraAttributes: ExperienceAttributes = {}): void {
		if (!event) {
			return;
		}

		if (event.action === 'taskSuccess') {
			this.succeed({});
		} else if (event.action === 'taskAbort') {
			this.abort({
				reason: event.reason,
				attributes: {
					...extraAttributes,
					stoppedOn: event.name,
					stoppedOnPath: this.getStoppedOnPath(event),
				},
				checkForTimeout: event.checkForTimeout,
			});
		} else if (event.action === 'taskFail') {
			this.fail({
				error: event.error,
				attributes: {
					...extraAttributes,
					stoppedOn: event.name,
					stoppedOnPath: this.getStoppedOnPath(event),
				},
			});
		}
	}

	updateAttributes(attributes: object): void {
		this.attributes = { ...this.attributes, ...attributes };
	}

	attachDebugPoint(message: string, attributes: ExperienceAttributes = {}): void {
		if (this.debugPoints?.some((debugPoint) => debugPoint.message === message)) {
			// Avoid adding duplicate debug points
			return;
		}

		this.debugPoints.push({
			timestamp: new Date().toISOString(),
			now: typeof window !== 'undefined' ? window.performance.now() : 0,
			message,
			attributes,
		});
	}

	getState(): ExperienceState {
		return {
			taskId: this.id,
			timeout: this.timeout,
			hasStopped: this.hasStopped,
			stopState: this.stopState,
			startTime: this.startTime,
			name: this.name,
			attributes: this.attributes,
		};
	}

	get hasStopped(): boolean {
		return this.stopState !== null;
	}

	private stop(event: StopEvent) {
		this.stopState = event.action;
		if (this.onStop) {
			this.onStop(event);
		}
		getFreezeTracker().unsubscribe(this.name);
	}

	private getAbsoluteDuration() {
		return Math.round(
			(typeof window !== 'undefined' ? window.performance.now() : 0) - this.startTime,
		);
	}

	private getDurationAdjustedForTabActivity() {
		return (
			this.getAbsoluteDuration() -
			getTabInactivityTracker().getInactiveMillisecondsSince(this.startTime)
		);
	}

	private getDurationAdjustedForActive() {
		return this.getAbsoluteDuration() - this.freezeTime;
	}

	private getStoppedOnPath(event: ExperienceEvent) {
		if (!event.attributes?.stoppedOnPath) {
			return event.name;
		}
		return `${event.name},${event.attributes.stoppedOnPath}`;
	}

	// Include certain broad-impact feature gates in experience events
	private getFeatureGateAttributes = memoize(() => {
		return {
			isContentWrapperEnabled: true,
			isNav4Enabled: true,
		};
	});
}
